import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BottomToast,
  BottomToastHandle,
} from '@components/shared/BottomToast.web';
import {
  CLIPBOARD_FAILURE_MESSAGE_KEYS,
  ClipboardFailureReason,
  copyTextToClipboard,
} from '@utils/copyTextToClipboard';

/**
 * Copies a share link and, on failure, shows the red-icon BottomToast used
 * by MediaViewer's copy-link (TA-10845). A missing link counts as a
 * link-generation failure, so callers can pass a nullable mutation result
 * straight through instead of silently skipping it.
 *
 * Render `failureToast` once in the caller's tree.
 */
export const useCopyLinkWithFailureToast = () => {
  const { t } = useTranslation();
  const toastRef = useRef<BottomToastHandle>(null);

  const showFailure = useCallback(
    (reason: ClipboardFailureReason) => {
      toastRef.current?.show(t(CLIPBOARD_FAILURE_MESSAGE_KEYS[reason]), {
        duration: 3000,
        icon: 'warning',
      });
    },
    [t]
  );

  /** Resolves `true` only when the link actually landed on the clipboard. */
  const copyLink = useCallback(
    async (link: string | null | undefined): Promise<boolean> => {
      if (!link) {
        showFailure('link-generation-failed');
        return false;
      }
      const result = await copyTextToClipboard(link);
      if (!result.success) {
        showFailure(result.reason);
        return false;
      }
      return true;
    },
    [showFailure]
  );

  const failureToast = <BottomToast ref={toastRef} />;

  return { copyLink, showFailure, failureToast };
};
